/**
 * 分拣过程动画 —— mock 过程帧生成器（ProcessFrame[] 基准/增量形态）
 *
 * 后端 `POST /sorting-plan/process-timeline` 未就绪时的兜底数据源（design 决策 7）：
 * 严格复刻 mock.ts 的算法（分组 = recommendPallets；合并 = mock.buildMerge：SKU 组内 +
 * tightness 降序贪心 + 每轮每盘至多一次 + 唛头约束三闸），但产出 **contract ProcessFrame[]**
 * （基准帧 + 增量帧），与 `contract-sample*.json` 同构——这样过渡期 live demo 也走真实
 * 重建路径（Reconstructor），上线前即被持续验证。
 *
 * 与 mock.ts（产 Frame[]）的算法一致，差别仅在输出形态（base/delta vs 全量快照）。
 * 已知 mock 限制（见 fixtures.md）：oversized 补 seal-pallet delta；limit-overflow 静默不发；
 * mark-blocked 仅 large-group(qty>5) 前提下触发。
 */

import type { AlgorithmConfig, Mark } from '../types';
import type { FocusTarget, FrameChanges, ItemSnapshot, PalletShell, Phase, ProcessFrame, StepAction } from './types';

const round2 = (n: number): number => Math.round(n * 100) / 100;
const div6 = (n: number): number => Math.round(n * 1e6) / 1e6;

const detailId = (sku: string, prefix: string, s: number): string => `${sku}|${prefix}/${s}`;
const chipId = (sku: string, prefix: string): string => `mark:${sku}|${prefix}`;

interface WorkItem {
  id: string;
  sku: string;
  markPrefix: string;
  markSerialNo: string;
  vol: number;
  wt: number;
  palletNo?: string;
}

interface WorkPallet {
  palletNo: string;
  sku: string;
  status: 'open' | 'sealed' | 'removed';
  itemIds: string[];
}

/**
 * 生成过程帧序列（ProcessFrame[]，基准帧 + 增量帧）。
 * 分组逐件、合并逐对逐轮，raw/expanded/merge-init/final 为基准帧。
 */
export function generateProcessFrames(marks: Mark[], config: AlgorithmConfig): ProcessFrame[] {
  const maxV = config.palletVolumeLimit;
  const maxW = config.palletWeightLimit;
  const frames: ProcessFrame[] = [];
  let seq = -1;
  const next = (): number => ++seq;
  let palletIdx = 0;
  const palletNo = (): string => `pallet-${String(++palletIdx).padStart(4, '0')}`;

  // ---- 明细件 + 唛头件数（largeGroup 用）----
  const items: WorkItem[] = [];
  const byId = new Map<string, WorkItem>();
  const markPrefixQuantity = new Map<string, number>();
  for (const m of marks) {
    const sku = m.sku ?? '';
    markPrefixQuantity.set(`${sku}|${m.markPrefix}`, m.quantity);
    for (let s = 1; s <= m.quantity; s++) {
      const it: WorkItem = {
        id: detailId(sku, m.markPrefix, s), sku, markPrefix: m.markPrefix,
        markSerialNo: String(s), vol: m.volumePerUnit, wt: m.weightPerUnit,
      };
      items.push(it);
      byId.set(it.id, it);
    }
  }

  // ---- recommendPallets 分配（权威锚点：item id -> palletNo）----
  const recAssign = new Map<string, string>();
  for (const m of marks) {
    const sku = m.sku ?? '';
    const oversized = m.volumePerUnit > maxV || m.weightPerUnit > maxW;
    if (oversized) {
      for (let s = 1; s <= m.quantity; s++) recAssign.set(detailId(sku, m.markPrefix, s), palletNo());
      continue;
    }
    let cur: { no: string; vol: number; wt: number } | null = null;
    for (let s = 1; s <= m.quantity; s++) {
      if (!cur || cur.vol + m.volumePerUnit > maxV || cur.wt + m.weightPerUnit > maxW) {
        cur = { no: palletNo(), vol: 0, wt: 0 };
      }
      cur.vol += m.volumePerUnit;
      cur.wt += m.weightPerUnit;
      recAssign.set(detailId(sku, m.markPrefix, s), cur.no);
    }
  }

  // ---- 可变工作态 ----
  const pallets: WorkPallet[] = [];
  const palletByNo = new Map<string, WorkPallet>();
  const openP = (no: string, sku: string): WorkPallet => {
    const p: WorkPallet = { palletNo: no, sku, status: 'open', itemIds: [] };
    pallets.push(p);
    palletByNo.set(no, p);
    return p;
  };
  const assignIt = (id: string, no: string): void => {
    byId.get(id)!.palletNo = no;
    palletByNo.get(no)!.itemIds.push(id);
  };
  const totalsOf = (p: WorkPallet): { w: number; v: number; marks: Set<string> } => {
    let w = 0;
    let v = 0;
    const marks = new Set<string>();
    for (const id of p.itemIds) {
      const it = byId.get(id)!;
      w += it.wt;
      v += it.vol;
      marks.add(it.markPrefix);
    }
    return { w, v, marks };
  };
  const shellOf = (p: WorkPallet): PalletShell => ({
    palletNo: p.palletNo, sku: p.sku, maxVolume: maxV, maxWeight: maxW, status: p.status,
  });
  const snapshotItems = (): ItemSnapshot[] => items.map(it => ({
    id: it.id, sku: it.sku, markPrefix: it.markPrefix, markSerialNo: it.markSerialNo,
    volume: it.vol, weight: it.wt, ...(it.palletNo != null ? { palletNo: it.palletNo } : {}),
  }));
  const snapshotPallets = (): PalletShell[] => pallets.filter(p => p.status !== 'removed').map(shellOf);

  const emit = (
    phase: Phase, action: StepAction, base: boolean,
    extra: { focus?: FocusTarget; decision?: ProcessFrame['decision']; items?: ItemSnapshot[]; pallets?: PalletShell[]; changes?: FrameChanges },
  ): void => {
    frames.push({ seq: next(), phase, action, base, ...extra });
  };

  const startPallet = (no: string, sku: string): void => emit('recommend', 'start-pallet', false, {
    focus: { type: 'pallet', palletNo: no },
    changes: { palletOpens: [{ palletNo: no, sku, maxVolume: maxV, maxWeight: maxW, status: 'open' }] },
  });
  const sealPallet = (no: string): void => emit('recommend', 'seal-pallet', false, {
    focus: { type: 'pallet', palletNo: no }, changes: { palletSeals: [no] },
  });
  const loaded = (id: string, no: string): void => emit('recommend', 'loaded', false, {
    focus: { type: 'item', itemId: id }, changes: { itemMoves: [{ id, palletNo: no }] },
  });
  const tryLoadOk = (p: WorkPallet, it: WorkItem): void => {
    const t = totalsOf(p);
    const nv = t.v + it.vol;
    const nw = t.w + it.wt;
    emit('recommend', 'try-load', false, {
      focus: { type: 'item', itemId: it.id },
      decision: {
        kind: 'capacity-ok',
        text: `体 ${round2(t.v)}+${round2(it.vol)}=${round2(nv)}≤${maxV} · 重 ${round2(t.w)}+${round2(it.wt)}=${round2(nw)}≤${maxW} ✓`,
        values: { newVolume: round2(nv), newWeight: round2(nw), volumeLimit: maxV, weightLimit: maxW },
      },
      changes: {},
    });
  };
  const tryLoadOverflow = (p: WorkPallet, it: WorkItem): void => {
    const t = totalsOf(p);
    const nv = t.v + it.vol;
    const nw = t.w + it.wt;
    const ov = nv > maxV;
    const ow = nw > maxW;
    emit('recommend', 'try-load', false, {
      focus: { type: 'item', itemId: it.id },
      decision: {
        kind: 'capacity-overflow',
        text: `体 ${round2(nv)}${ov ? '>' : '≤'}${maxV} · 重 ${round2(nw)}${ow ? '>' : '≤'}${maxW} ✗ 装不下`,
        values: { newVolume: round2(nv), newWeight: round2(nw), volumeLimit: maxV, weightLimit: maxW },
      },
      changes: {},
    });
  };

  // ---- raw 基准帧 ----
  emit('raw', 'init', true, {
    items: marks.map(m => ({
      id: chipId(m.sku ?? '', m.markPrefix), sku: m.sku ?? '', markPrefix: m.markPrefix, markSerialNo: '',
      volume: round2(m.volumePerUnit * m.quantity), weight: round2(m.weightPerUnit * m.quantity), quantity: m.quantity,
    })),
    pallets: [],
  });

  // ---- expanded 基准帧 ----
  emit('expanded', 'init', true, {
    items: items.map(it => ({
      id: it.id, sku: it.sku, markPrefix: it.markPrefix, markSerialNo: it.markSerialNo,
      volume: it.vol, weight: it.wt,
    })),
    pallets: [],
  });

  // ---- 分组逐件（镜像 mock.buildGrouping）----
  let openPallet: WorkPallet | null = null;
  for (const m of marks) {
    const sku = m.sku ?? '';
    const oversized = m.volumePerUnit > maxV || m.weightPerUnit > maxW;
    for (let s = 1; s <= m.quantity; s++) {
      const id = detailId(sku, m.markPrefix, s);
      const it = byId.get(id)!;
      const targetNo = recAssign.get(id)!;
      if (oversized) {
        const p = openP(targetNo, sku);
        startPallet(targetNo, sku);
        assignIt(id, targetNo);
        emit('recommend', 'oversized', false, {
          focus: { type: 'item', itemId: id },
          decision: {
            kind: 'oversized',
            text: `单件 ${it.vol}CBM/${it.wt}KGS 超限，独占一盘`,
            values: { volume: it.vol, weight: it.wt, volumeLimit: maxV, weightLimit: maxW },
          },
          changes: { itemMoves: [{ id, palletNo: targetNo }] },
        });
        p.status = 'sealed';
        sealPallet(targetNo); // base/delta：open→sealed 必须发 seal delta（replay 不变量）
        openPallet = null;
        continue;
      }
      const curNo = openPallet ? openPallet.palletNo : null;
      if (curNo === targetNo) {
        tryLoadOk(openPallet!, it);
        assignIt(id, targetNo);
        loaded(id, targetNo);
      } else {
        if (openPallet) {
          tryLoadOverflow(openPallet, it);
          openPallet.status = 'sealed';
          sealPallet(openPallet.palletNo);
        }
        const p = openP(targetNo, sku);
        startPallet(targetNo, sku);
        tryLoadOk(p, it);
        assignIt(id, targetNo);
        loaded(id, targetNo);
        openPallet = p;
      }
    }
    if (openPallet) {
      openPallet.status = 'sealed';
      sealPallet(openPallet.palletNo);
      openPallet = null;
    }
  }

  // ---- merge 基准帧（合并起点 checkpoint）----
  emit('merge', 'init', true, { items: snapshotItems(), pallets: snapshotPallets() });

  // ---- 合并逐对逐轮（镜像 mock.buildMerge）----
  const evalGates = (
    a: WorkPallet, b: WorkPallet, largeGroup: Set<string>,
  ): 'ok' | 'limit-overflow' | 'mark-blocked' => {
    const at = totalsOf(a);
    const bt = totalsOf(b);
    if (at.w + bt.w > maxW || at.v + bt.v > maxV) return 'limit-overflow';
    if (largeGroup.size > 0) {
      const combined = new Set<string>([...at.marks, ...bt.marks]);
      const hasLarge = [...combined].some(mk => largeGroup.has(mk));
      if (hasLarge && combined.size > 2) return 'mark-blocked';
    }
    return 'ok';
  };
  const tightness = (a: WorkPallet, b: WorkPallet): number => {
    const at = totalsOf(a);
    const bt = totalsOf(b);
    return Math.max((at.w + bt.w) / maxW, (at.v + bt.v) / maxV);
  };

  const pushMergeEvalOk = (target: WorkPallet, source: WorkPallet, t: number): void => {
    const at = totalsOf(target);
    const bt = totalsOf(source);
    emit('merge', 'merge-eval', false, {
      focus: { type: 'pair', pair: { target: target.palletNo, source: source.palletNo, tightness: div6(t) } },
      decision: {
        kind: 'merge-ok',
        text: `三闸通过 · 紧度 ${t.toFixed(6)}`,
        values: { tightness: div6(t), weightUtil: div6((at.w + bt.w) / maxW), volumeUtil: div6((at.v + bt.v) / maxV) },
      },
      changes: {},
    });
  };
  const pushMergeEvalBlocked = (a: WorkPallet, b: WorkPallet): void => {
    const combined = new Set<string>([...totalsOf(a).marks, ...totalsOf(b).marks]);
    emit('merge', 'merge-eval', false, {
      focus: { type: 'pair', pair: { target: a.palletNo, source: b.palletNo, tightness: 0 } },
      decision: { kind: 'mark-blocked', text: `distinct=${combined.size} > 2 ✗ 唛头约束阻断`, values: { distinct: combined.size, limit: 2 } },
      changes: {},
    });
  };
  const pushMergePick = (target: WorkPallet, source: WorkPallet, t: number): void => emit('merge', 'merge-pick', false, {
    focus: { type: 'pair', pair: { target: target.palletNo, source: source.palletNo, tightness: div6(t) } },
    changes: {},
  });
  const pushMergeAbsorb = (target: WorkPallet, source: WorkPallet): void => {
    const moved = [...source.itemIds];
    for (const id of moved) {
      byId.get(id)!.palletNo = target.palletNo;
      target.itemIds.push(id);
    }
    source.itemIds = [];
    source.status = 'removed';
    emit('merge', 'merge-absorb', false, {
      focus: { type: 'pallet', palletNo: target.palletNo },
      changes: {
        itemMoves: moved.map(id => ({ id, palletNo: target.palletNo })),
        palletRemoves: [source.palletNo],
      },
    });
    const idx = pallets.indexOf(source);
    pallets.splice(idx, 1);
    palletByNo.delete(source.palletNo);
  };

  let anyMergeFrame = false;
  let markBlockedIllustrated = false;
  const groupMap = new Map<string, WorkPallet[]>();
  for (const p of pallets) {
    if (!groupMap.has(p.sku)) groupMap.set(p.sku, []);
    groupMap.get(p.sku)!.push(p);
  }
  for (const group of groupMap.values()) {
    if (group.length < 2) continue;
    const sku = group[0].sku;
    const largeGroup = new Set<string>();
    const prefixes = new Set<string>();
    for (const p of group) for (const id of p.itemIds) prefixes.add(byId.get(id)!.markPrefix);
    for (const mp of prefixes) {
      const qty = markPrefixQuantity.get(`${sku}|${mp}`);
      if (qty != null && qty > 5) largeGroup.add(mp);
    }
    let candidates = [...group];
    while (true) {
      const feasible: { target: WorkPallet; source: WorkPallet; tightness: number }[] = [];
      const markBlocked: { a: WorkPallet; b: WorkPallet }[] = [];
      for (let i = 0; i < candidates.length; i++) {
        for (let j = i + 1; j < candidates.length; j++) {
          const a = candidates[i];
          const b = candidates[j];
          const gate = evalGates(a, b, largeGroup);
          if (gate === 'ok') feasible.push({ target: a, source: b, tightness: tightness(a, b) });
          else if (gate === 'mark-blocked') markBlocked.push({ a, b });
        }
      }
      if (feasible.length === 0) {
        if (!markBlockedIllustrated && markBlocked.length > 0) {
          pushMergeEvalBlocked(markBlocked[0].a, markBlocked[0].b);
          markBlockedIllustrated = true;
          anyMergeFrame = true;
        }
        break;
      }
      feasible.sort((x, y) => y.tightness - x.tightness);
      const used = new Set<string>();
      const chosen: typeof feasible = [];
      for (const pair of feasible) {
        if (used.has(pair.target.palletNo) || used.has(pair.source.palletNo)) continue;
        chosen.push(pair);
        used.add(pair.target.palletNo);
        used.add(pair.source.palletNo);
      }
      if (!markBlockedIllustrated && markBlocked.length > 0) {
        pushMergeEvalBlocked(markBlocked[0].a, markBlocked[0].b);
        markBlockedIllustrated = true;
      }
      for (const pair of chosen) {
        pushMergeEvalOk(pair.target, pair.source, pair.tightness);
        pushMergePick(pair.target, pair.source, pair.tightness);
        pushMergeAbsorb(pair.target, pair.source);
        anyMergeFrame = true;
      }
      emit('merge', 'merge-round-end', false, { changes: {} });
      candidates = pallets.filter(p => p.sku === sku);
    }
  }
  if (!anyMergeFrame) {
    // 无可合并对合成帧（mock.buildMerge 末尾兜底；后端形态待确认）
    emit('merge', 'merge-eval', false, { changes: {} });
  }

  // ---- final 基准帧 ----
  emit('final', 'final', true, { items: snapshotItems(), pallets: snapshotPallets() });

  return frames;
}
